import { Component, Input, Output, OnChanges, SimpleChanges, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { delay, finalize, map, tap } from 'rxjs/operators';

import { DocumentInput, EmailTemplate, EmailTemplateTypeEnum, User } from '@app/models';
import { EmailTemplateService } from '@app/services';
import { SelectOption } from '@app/shared';

@Component({
  selector: 'app-select-email-template',
  templateUrl: './select-email-template.component.html',
  styleUrls: ['./select-email-template.component.scss'],
  standalone: false
})
export class SelectEmailTemplateComponent implements OnChanges {
  @Input() user: User;
  @Input() document: DocumentInput;
  @Input() isNewDocument = false;
  @Output() emailTemplateChange = new EventEmitter<EmailTemplate>();
  @Output() documentChange = new EventEmitter<DocumentInput>();
  _document: DocumentInput;
  emailTemplateSelected: EmailTemplate;
  emailTemplates: EmailTemplate[];
  defaultEmailTemplate: EmailTemplate;
  selectedLang: string;
  firstTime = true;

  constructor(public translateService: TranslateService, private emailTemplateService: EmailTemplateService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.user?.previousValue && changes.user?.currentValue) {
      this.defaultEmailTemplate = {
        enUS: this.user.settings.email,
        ptBR: this.user.settings.email
      };
    }

    if (changes.document?.currentValue) {
      this._document = this.document;
      this.selectedLang = (this._document.locale?.language || '').replace('-', '');

      if (this.user) {
        this._document.message = this.document.message || this.user.settings.email.text;
        this.documentChange.emit(this._document);
      }
    }
  }

  emailTemplatesRequest(search: string): Observable<SelectOption[]> {
    if (this.firstTime && this.isNewDocument) {
      const userMemberGroup = this.user?.member?.group;
      let emailTemplateSolicitation = userMemberGroup?.email_templates?.find(t => t.type === EmailTemplateTypeEnum.Solicitation);

      if (!this.user?.subscription?.has_premium_features) {
        emailTemplateSolicitation = null;
      }
      if (userMemberGroup?.styles?.overwrite_email && emailTemplateSolicitation) {
        search = emailTemplateSolicitation.name;
      }

      this._document.email_template_id = emailTemplateSolicitation?.id || this._document.email_template_id || -1;
      this.documentChange.emit(this._document);
    }

    return this.emailTemplateService.emailTemplates({ page: 1, limit: 8, search }).pipe(
      delay(0),
      map(page => (page.data || []).filter(item => !!item && item.type === EmailTemplateTypeEnum.Solicitation)),
      tap(templates => (this.emailTemplates = templates)),
      map(templates => [{ key: -1, value: this.translateService.instant('settings.defaultTemplate') }, ...templates.map(data => ({ key: data.id, value: data.name }))]),
      finalize(() => this.firstTime && this.selectEmailTemplate(true))
    );
  }

  selectEmailTemplate(fixedMessage?: boolean) {
    if (this._document?.email_template_id === -1 && this.defaultEmailTemplate) {
      this.emailTemplateSelected = this.defaultEmailTemplate;
    } else {
      this.emailTemplateSelected = this.emailTemplates.find(item => item.id === this._document?.email_template_id) || this.defaultEmailTemplate;
    }

    if (!fixedMessage || this.firstTime) {
      this._document.message = this.emailTemplateSelected[this.selectedLang as 'ptBR' | 'enUS'].text;
      this.documentChange.emit(this._document);
    }
    this.firstTime = false;

    this.emailTemplateChange.emit(this.emailTemplateSelected);
  }
}
